import React, { useState, useRef, useEffect, useCallback } from 'react';
import './LedgerDatePicker.css';

// A small, dependency-free calendar popover for the "jump to date" control.
//
// Why not <input type="date">? The native popup's month-navigation arrows
// are part of the browser chrome we can't control, and selecting/stepping
// fires `change` ambiguously. Here we own every interaction:
//   - The ‹ › header buttons ONLY change the displayed month.
//   - Clicking a DAY cell is the ONLY thing that commits a selection.
// So nothing jumps until the user actually clicks a day.
//
// Props:
//   value     — 'YYYY-MM-DD' selected day, or '' for none (latest)
//   max       — 'YYYY-MM-DD' latest selectable day (future days disabled)
//   disabled  — when true, the trigger is inert
//   onSelect  — called with 'YYYY-MM-DD' on a day click, or '' on Clear

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const pad = (n) => String(n).padStart(2, '0');
const toStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`; // m is 0-indexed
const parse = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return { y, m: m - 1, d };
};

function LedgerDatePicker({ value, max, disabled, onSelect }) {
  const [open, setOpen] = useState(false);
  // The month currently shown in the grid — view state only, never commits.
  const initial = parse(value) || parse(max) || (() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  })();
  const [view, setView] = useState({ y: initial.y, m: initial.m });
  const rootRef = useRef(null);

  // Re-centre the grid on the selected month each time the popover opens.
  useEffect(() => {
    if (!open) return;
    const sel = parse(value) || parse(max);
    if (sel) setView({ y: sel.y, m: sel.m });
  }, [open, value, max]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const maxParts = parse(max);
  const isAfterMax = useCallback((y, m, d) => {
    if (!maxParts) return false;
    if (y !== maxParts.y) return y > maxParts.y;
    if (m !== maxParts.m) return m > maxParts.m;
    return d > maxParts.d;
  }, [maxParts]);

  const stepMonth = (delta) => {
    // View-only navigation. NEVER calls onSelect.
    setView((v) => {
      const next = new Date(v.y, v.m + delta, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });
  };

  const pickDay = (d) => {
    if (isAfterMax(view.y, view.m, d)) return;
    onSelect(toStr(view.y, view.m, d)); // the ONLY commit path
    setOpen(false);
  };

  // Build the day grid: leading blanks so the 1st lands under its weekday
  // (week starts Monday).
  const firstDow = (new Date(view.y, view.m, 1).getDay() + 6) % 7; // 0 = Mon
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const sel = parse(value);
  // Show the selected day as MM/DD/YYYY; otherwise the format itself as a
  // dimmed placeholder.
  const triggerLabel = sel
    ? `${pad(sel.m + 1)}/${pad(sel.d)}/${sel.y}`
    : 'MM/DD/YYYY';

  return (
    <div className="ldp" ref={rootRef}>
      <button
        type="button"
        className={`ldp-trigger${value ? ' ldp-trigger--set' : ''}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <svg className="ldp-cal-icon" width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {triggerLabel}
      </button>

      {open && (
        <div className="ldp-pop" role="dialog">
          <div className="ldp-head">
            <button type="button" className="ldp-nav" onClick={() => stepMonth(-1)} aria-label="Previous month">‹</button>
            <span className="ldp-title">{MONTHS[view.m]} {view.y}</span>
            <button type="button" className="ldp-nav" onClick={() => stepMonth(1)} aria-label="Next month">›</button>
          </div>

          <div className="ldp-weekdays">
            {WEEKDAYS.map((w, i) => <span key={i} className="ldp-weekday">{w}</span>)}
          </div>

          <div className="ldp-grid">
            {cells.map((d, i) => {
              if (d === null) return <span key={`b${i}`} className="ldp-cell ldp-cell--blank" />;
              const disabledDay = isAfterMax(view.y, view.m, d);
              const isSel = sel && sel.y === view.y && sel.m === view.m && sel.d === d;
              return (
                <button
                  key={d}
                  type="button"
                  className={`ldp-cell${isSel ? ' ldp-cell--sel' : ''}`}
                  disabled={disabledDay}
                  onClick={() => pickDay(d)}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <div className="ldp-foot">
            <button type="button" className="ldp-link" onClick={() => { onSelect(''); setOpen(false); }}>
              Latest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LedgerDatePicker;
